/**
 * Split-transaction draft operations (SEE-1821 / SEE-1598), served by the CDK v2Split
 * Lambda: read, autosave and clear the in-progress split for a New-status transaction.
 *
 * The draft document is ours to shape (`SplitDraftDocument`); the backend stores it as an
 * opaque JSON object under 64 KB and gates `saveSplitDraft` on the splitTransactions
 * AppConfig flag, New status, and the row not already being a split parent or child.
 * A refusal comes back as a GraphQL error with errorType ClientError and a user-visible
 * message; a tenant denial as Unauthorized.
 */

import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { fetchWithTransportRetry } from './networkTransport';
import type { BasicHttpResponse, SplitDraft as CdkSplitDraft } from 'CDK_API';

const CDK_APPSYNC_ENDPOINT = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

/** One line of a split: its amount in cents plus the coding the editor collects. */
export interface SplitDraftLine {
  /** Stable per-line key so React and the autosave diff can track reorders. */
  key: string;
  /** Cents. The last line is the locked remainder and is derived, never typed. */
  amountCents: number;
  /** The user typed a percentage rather than a flat amount (display only). */
  enteredAsPercent?: boolean;
  /** Coding ids, keyed by the same field names the transaction carries. */
  coding: Record<string, string | null>;
  note?: string | null;
  /** SEE-1601: this split's receipts; absent on drafts saved before the field existed. */
  receipts?: SplitDraftReceipt[];
  /** SEE-1605: the reviewer messages this split was returned with, once every split was
   *  returned and the split folded back into a draft. Shown at the top of the split. */
  returnMessages?: SplitReturnMessage[];
}

/** SEE-1605: one reviewer message on a returned split. */
export interface SplitReturnMessage {
  authorName?: string | null;
  authorRole?: string | null;
  message?: string | null;
  createdAt?: string | null;
}

/**
 * SEE-1601: a receipt on a split line. Before Submit all (SEE-1604) a receipt is only a
 * reference the draft holds: a pre-split receipt is still the parent's ReceiptUpload
 * ("parent"), and one uploaded inside a split is an unlinked ReceiptUpload ("split")
 * whose S3 object sits under the parent's folder. Submit all gives each child its own
 * ReceiptUpload + link per reference, so splits never share a receipt row.
 */
export interface SplitDraftReceipt {
  /** The ReceiptUpload id. */
  id: string;
  /** The S3 key (ReceiptUpload.document). */
  document: string;
  displayName?: string | null;
  /** ReceiptUpload.sizeBytes; null on receipts uploaded before the field existed. */
  sizeBytes?: number | null;
  source: 'parent' | 'split';
}

export interface SplitDraftDocument {
  version: 1;
  /** The parent's total in cents, captured when the draft was created. */
  totalCents: number;
  lines: SplitDraftLine[];
  /** An amount was typed, so the last line is a computed remainder (editor pill). */
  remainderAdjusted?: boolean;
  /** SEE-1601: the parent's receipts when the split began; copied into every new split. */
  parentReceipts?: SplitDraftReceipt[];
}

export interface SplitDraft {
  transactionId: string;
  paidolId: string;
  draft: SplitDraftDocument;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const splitDraftSelection = `
  transactionId
  paidolId
  draft
  createdBy
  createdAt
  updatedAt
`;

const getSplitDraftQuery = /* GraphQL */ `
  query GetSplitDraft($transactionId: ID!) {
    getSplitDraft(transactionId: $transactionId) {
      ${splitDraftSelection}
    }
  }
`;

const saveSplitDraftMutation = /* GraphQL */ `
  mutation SaveSplitDraft($input: SaveSplitDraftInput!) {
    saveSplitDraft(input: $input) {
      ${splitDraftSelection}
    }
  }
`;

const listSplitDraftsQuery = /* GraphQL */ `
  query ListSplitDrafts($paidolId: ID!) {
    listSplitDrafts(paidolId: $paidolId) {
      transactionIds
    }
  }
`;

const deleteSplitDraftMutation = /* GraphQL */ `
  mutation DeleteSplitDraft($transactionId: ID!) {
    deleteSplitDraft(transactionId: $transactionId) {
      statusCode
      body
    }
  }
`;

async function cdkRequest<T>(
  query: string,
  variables: Record<string, unknown>,
  operationName: string
): Promise<T> {
  if (!CDK_APPSYNC_ENDPOINT) {
    throw new Error('CDK AppSync endpoint is not configured');
  }
  const session = await Auth.currentSession();
  const jwt = session.getIdToken().getJwtToken();
  const response = await fetchWithTransportRetry(CDK_APPSYNC_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json', Authorization: jwt },
    body: JSON.stringify({ query, variables }),
  });
  const json = (await response.json()) as {
    data?: T;
    errors?: Array<{ message: string; errorType?: string }>;
  };
  reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName });
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join(', '));
  }
  if (!json.data) {
    throw new Error(`${operationName} returned no data`);
  }
  return json.data;
}

/**
 * AWSJSON arrives as one JSON string. A Lambda deployed before 2026-10-16 handed
 * AppSync a pre-serialised string, so the field came back encoded twice; parse until
 * we hold the document so a stale stage never breaks reopen.
 */
export function parseDraftDocument(raw: string): SplitDraftDocument {
  let value: unknown = raw;
  for (let i = 0; i < 2 && typeof value === 'string'; i += 1) value = JSON.parse(value);
  if (!value || typeof value !== 'object' || !Array.isArray((value as SplitDraftDocument).lines)) {
    throw new Error('Split draft is not readable');
  }
  return value as SplitDraftDocument;
}

function parseDraft(row: CdkSplitDraft): SplitDraft {
  return {
    transactionId: row.transactionId,
    paidolId: row.paidolId,
    draft: parseDraftDocument(row.draft),
    createdBy: row.createdBy ?? null,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

/** The draft for a transaction, or null when none exists (the "has draft" signal). */
export async function getSplitDraft(transactionId: string): Promise<SplitDraft | null> {
  const data = await cdkRequest<{ getSplitDraft: CdkSplitDraft | null }>(
    getSplitDraftQuery,
    { transactionId },
    'getSplitDraft'
  );
  return data.getSplitDraft ? parseDraft(data.getSplitDraft) : null;
}

/** SEE-1828: the transaction ids of a paidol's drafts, for the table's draft indicator. */
export async function listSplitDraftIds(paidolId: string): Promise<string[]> {
  const data = await cdkRequest<{ listSplitDrafts: { transactionIds: string[] } | null }>(
    listSplitDraftsQuery,
    { paidolId },
    'listSplitDrafts'
  );
  return data.listSplitDrafts?.transactionIds ?? [];
}

/** Create or replace the draft. Throws with the backend's reason when refused. */
export async function saveSplitDraft(transactionId: string, draft: SplitDraftDocument): Promise<SplitDraft> {
  const data = await cdkRequest<{ saveSplitDraft: CdkSplitDraft }>(
    saveSplitDraftMutation,
    { input: { transactionId, draft: JSON.stringify(draft) } },
    'saveSplitDraft'
  );
  return parseDraft(data.saveSplitDraft);
}

/** Clear the draft. Idempotent: resolves whether or not one existed. */
export async function deleteSplitDraft(transactionId: string): Promise<void> {
  const data = await cdkRequest<{ deleteSplitDraft: BasicHttpResponse }>(
    deleteSplitDraftMutation,
    { transactionId },
    'deleteSplitDraft'
  );
  const status = data.deleteSplitDraft?.statusCode;
  if (status !== 200) {
    throw new Error(data.deleteSplitDraft?.body || `deleteSplitDraft failed (${status})`);
  }
}
